import cx from 'classnames';
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

interface ChatInputProps {
  onSubmit: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Display name of the agent. Used in the disclaimer line below the
   * input pill.
   */
  agentName?: string;
}

const DEFAULT_PLACEHOLDER = 'Ask anything...';
const DEFAULT_AGENT_NAME = 'Routine Agent';

/**
 * Chat composer. Pill-shaped textarea with an auto-growing height
 * (20→80px), a 30×30 brand-orange send button, and a small
 * disclaimer line beneath the input.
 *
 * Layout details:
 *
 *  - Outer container: `10px 14px 12px` padding, flex column.
 *  - Pill: `--fill-primary` background, radius morphs `999 → 20`
 *    as content lands so it transitions from chip to chat-bubble.
 *  - Textarea: 14px text, line-height 1.4, auto-grows up to 80px
 *    so multi-line input never pushes the disclaimer off-screen.
 *  - Send button: brand `--orange` when active, `--fill-secondary`
 *    when disabled; the glyph uses `currentColor`.
 *  - Disclaimer: 10px, `--label-quaternary`, centered.
 */
export function ChatInput({
  onSubmit,
  disabled = false,
  placeholder = DEFAULT_PLACEHOLDER,
  agentName = DEFAULT_AGENT_NAME,
}: ChatInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Resize the textarea up to a max of 80px so it grows to fit
  // multi-line input without ever pushing the disclaimer off-screen.
  // useLayoutEffect — measure + mutate before paint so each keystroke
  // doesn't render a single frame at the stale height.
  useLayoutEffect(() => {
    const el = textareaRef.current;

    if (el === null) return;

    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 80)}px`;
  }, [value]);

  const hasContent = value.trim().length > 0;
  const isSubmitDisabled = disabled || !hasContent;

  const handleSubmit = useCallback(() => {
    if (disabled) return;

    const next = value.trim();
    if (next.length === 0) return;

    setValue('');
    onSubmit(next);
  }, [disabled, value, onSubmit]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      const { nativeEvent } = event;
      // Don't submit during an IME composition (CJK input flow on iPad
      // Safari fires keydown with keyCode 229 mid-composition).
      if (nativeEvent.isComposing || nativeEvent.keyCode === 229) return;

      event.preventDefault();
      handleSubmit();
    },
    [handleSubmit],
  );

  return (
    <div
      className="tw-pt-10 tw-pb-12 tw-shrink-0"
      // Horizontal padding 14px isn't in the design-system scale
      // (which jumps 12 → 16), so it stays inline.
      style={{ paddingLeft: 14, paddingRight: 14 }}
    >
      <div
        className={cx('tw-flex', 'tw-items-center', 'tw-gap-6', 'tw-p-6')}
        style={{
          // Animated radius (chip ↔ chat-bubble) and the prototype's
          // exact cubic-bezier curve don't map to design-system tokens.
          // The pill background uses the themeable `--fill-primary` token.
          background: 'var(--fill-primary)',
          borderRadius: hasContent ? 20 : 999,
          transition: 'border-radius 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={disabled}
          className={cx(
            'tw-flex-1',
            'tw-bg-transparent',
            'tw-border-none',
            'tw-outline-none',
            'tw-resize-none',
            'tw-pl-8',
          )}
          style={{
            // 14px font size + 1.4 line-height + 20→80px auto-grow
            // band are prototype-specific (the typography scale
            // jumps 13 → 15, and there are no min/max-height tokens
            // at these values).
            fontSize: 14,
            fontWeight: 400,
            color: 'var(--label-primary)',
            lineHeight: 1.4,
            minHeight: 20,
            maxHeight: 80,
            fontFamily: 'inherit',
          }}
        />

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          aria-label="Send message"
          className={cx(
            'tw-flex',
            'tw-items-center',
            'tw-justify-center',
            'tw-shrink-0',
            'tw-rounded-full',
            'tw-border-none',
            !isSubmitDisabled && 'tw-text-light',
          )}
          style={{
            // 30×30 isn't in the spacing scale (28 → 32); active /
            // disabled background swap and the transition curve stay
            // inline. The glyph uses `currentColor` so it tracks the
            // button's text color (light on the orange active state,
            // muted on the disabled fill) in both themes.
            width: 30,
            height: 30,
            background: isSubmitDisabled
              ? 'var(--fill-secondary)'
              : 'var(--orange)',
            color: isSubmitDisabled ? 'var(--label-quaternary)' : undefined,
            cursor: isSubmitDisabled ? 'default' : 'pointer',
            transition: 'background 0.2s ease',
          }}
        >
          <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
            <path
              d="M8 13V3M3.5 7.5L8 3l4.5 4.5"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div
        className="tw-mt-6 tw-text-center"
        style={{
          // 10px font + 1.3 line-height aren't in the typography scale.
          // The disclaimer text uses the themeable `--label-quaternary`
          // token.
          fontSize: 10,
          color: 'var(--label-quaternary)',
          lineHeight: 1.3,
        }}
      >
        {agentName} is an AI and can make mistakes. Please double check
        responses.
      </div>
    </div>
  );
}
